/**
 * submitAppointmentRequest: 空き枠がない場合のアポイントリクエスト（仮予約）を送信する。
 * US-D2-04、FR-M2。BR-U6-2: ログイン検証。
 */

import { relayClient } from "../core/relayClient";
import { classifyError, logClientWarning, toFailureKind } from "../core/errorHandler";
import { isLoggedIn } from "../core/authState";
import type { MeetingType, FailureKind } from "../types";

export interface SubmitAppointmentRequestParams {
  memberGroupId: string;
  desiredDateTime1: string;
  desiredDateTime2?: string;
  desiredDateTime3?: string;
  screenType: string;
  objectType: string;
  objectRootId: string;
  scenarioId?: string;
  /** 商談形式（2026-08-04対面商談対応〔U12〕BR-U6-12により追加、必須）。 */
  meetingType: MeetingType;
  /** ご要望・メッセージ（任意、ラベルはmeetingTypeで分岐。2026-08-04追加）。 */
  message?: string;
  /** ご訪問希望場所（meetingType=ON_SITEの場合のみ、任意。2026-08-04追加）。 */
  visitPreferredLocation?: string;
  /**
   * 特設サイトフラグ（U13 site_type統一Unit、2026-08-04追加、任意）。
   * ContentDisplayResult.prFlgをそのまま渡す。中継エンドポイント（HCAPI）がsite_type/サブID解決の
   * 起点として使用する。
   */
  prFlg?: boolean | null;
  /** レコメンドCV経由のアポイントリクエストかどうか（任意、デフォルトfalse）。 */
  isRecommendCv?: boolean;
  /**
   * アンケート回答ログID（survey_response_log.id、任意）。アンケート回答経由でこの導線に進んだ場合のみ
   * `submitSurveyAnswer`の戻り値から引き継いで送信する。アンケートを経由しない導線では渡さない
   * （未指定＝紐付けなし。2026-09-24 BR-U2-21 誤紐付け修正）。
   */
  surveyResponseLogId?: string;
}

export interface SubmitAppointmentRequestResult {
  success: boolean;
  requiresLogin: boolean;
  appointmentId?: string;
  /**
   * 同一企業への重複アポイント（BR-U2-18）。既存の有効アポイントが存在するため作成が拒否された場合に設定。
   * `existingAppointmentId`はキャンセル導線（「こちら」リンク）に用いる。
   */
  duplicateCompany?: { existingAppointmentId: string };
  /**
   * 効果分析ログの失敗大分類（VALIDATION/SLOT_UNAVAILABLE/DUPLICATE/SYSTEM/AUTH、BR-AN-6）。
   * 失敗時のみ設定。APPOINTMENT_SUBMIT_FAILED イベントの発火に用いる（呼び出し元）。
   */
  failureKind?: FailureKind;
  /** 効果分析ログの業務エラーコード（spica-apiのErrorCode、BR-AN-6）。失敗時に取得できれば設定。 */
  businessErrorCode?: string;
}

interface CreateAppointmentRequestData {
  id: string;
}

export async function submitAppointmentRequest(
  params: SubmitAppointmentRequestParams
): Promise<SubmitAppointmentRequestResult> {
  if (!isLoggedIn()) {
    return { success: false, requiresLogin: true };
  }

  const response = await relayClient.createAppointmentRequest<CreateAppointmentRequestData>(params);

  if (response.status === 401) {
    return { success: false, requiresLogin: true };
  }

  if (!response.ok || !response.data) {
    const classified = classifyError(response);
    // 効果分析ログ（BR-AN-6）: 失敗の大分類・業務エラーコードを返り値へ透過する。
    const failureKind = toFailureKind(classified);
    const businessErrorCode = classified.businessErrorCode;
    logClientWarning(`submitAppointmentRequest failed: ${classified.kind}`);
    if (
      classified.businessErrorCode === "DUPLICATE_COMPANY_APPOINTMENT" &&
      response.existingAppointmentId
    ) {
      return {
        success: false,
        requiresLogin: false,
        duplicateCompany: { existingAppointmentId: response.existingAppointmentId },
        failureKind,
        businessErrorCode,
      };
    }
    return { success: false, requiresLogin: false, failureKind, businessErrorCode };
  }

  return { success: true, requiresLogin: false, appointmentId: response.data.id };
}
